import { useEffect, useRef } from "react"
import { useFetchers } from "react-router"
import { AnalyticsEvents } from "./analytics"
import { useUmami } from "./umami"
import { useOptionalUser } from "./user"

/**
 * Identifies the current user session in Umami analytics.
 * Fires a login event once per browser session when a user transitions
 * from anonymous to authenticated (uses sessionStorage to distinguish
 * fresh logins from page reloads with an existing session).
 */
export function useIdentifyUser() {
	const { umami } = useUmami()
	const user = useOptionalUser()
	const identifiedRef = useRef<string | null>(null)

	useEffect(() => {
		if (user && identifiedRef.current !== user.id) {
			umami().identify(user.id, {
				name: user.name ?? undefined,
				role: user.roles?.[0]?.name ?? "user",
			})

			const wasIdentified = sessionStorage.getItem("umami_identified")
			if (wasIdentified !== user.id) {
				umami().track(AnalyticsEvents.LOGIN, {
					user_id: user.id,
				})
				sessionStorage.setItem("umami_identified", user.id)
			}

			identifiedRef.current = user.id
		}
		if (!user && identifiedRef.current) {
			identifiedRef.current = null
			sessionStorage.removeItem("umami_identified")
		}
	}, [user?.id])
}

/**
 * Tracks add-to-cart events globally by watching fetchers that submit
 * to /cart with intent "add-to-cart". Placed in root.tsx so it captures
 * add-to-cart from any page (product pages, quick-add, etc.).
 */
export function useTrackAddToCart() {
	const { umami } = useUmami()
	const fetchers = useFetchers()
	const trackedRef = useRef(new Set<string>())

	useEffect(() => {
		for (const f of fetchers) {
			if (
				f.formAction === "/cart" &&
				f.formData?.get("intent") === "add-to-cart" &&
				f.state === "loading"
			) {
				const productId = f.formData?.get("productId") as string
				const variantId = (f.formData?.get("variantId") as string) || undefined
				const key = productId + (variantId ?? "")

				if (!trackedRef.current.has(key)) {
					umami().track(AnalyticsEvents.ADD_TO_CART, {
						product_id: productId,
						variant_id: variantId,
					})
					trackedRef.current.add(key)

					// Reset dedup after a short delay so the same product
					// can be added again in a subsequent action
					setTimeout(() => {
						trackedRef.current.delete(key)
					}, 2000)
				}
			}
		}
	}, [fetchers])
}

/**
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%) for content pages.
 * Each milestone fires once per page load.
 */
export function useTrackScrollDepth(contentId: string) {
	const { umami } = useUmami()

	useEffect(() => {
		const milestones = [25, 50, 75, 100]
		const fired = new Set<number>()

		function handleScroll() {
			const scrollTop = window.scrollY
			const docHeight =
				document.documentElement.scrollHeight - window.innerHeight
			if (docHeight <= 0) return

			const scrollPercent = Math.round((scrollTop / docHeight) * 100)

			for (const milestone of milestones) {
				if (scrollPercent >= milestone && !fired.has(milestone)) {
					fired.add(milestone)
					umami().track(AnalyticsEvents.BLOG_SCROLL_DEPTH, {
						content_id: contentId,
						depth: milestone,
					})
				}
			}
		}

		window.addEventListener("scroll", handleScroll, { passive: true })
		return () => window.removeEventListener("scroll", handleScroll)
	}, [contentId])
}
